import { useEffect, useRef } from 'react';
import { t } from 'i18next';
// configs
import { EX7767 } from 'config/flags';
// components
import { Form, IconButton, Panel } from 'rsuite';
import { ExInputUploader, FormInput } from '@commons/EXComponentsLibrary';
// utils && hooks
import { handleDownload } from './utils/handleDownload';
import { useDocumentation } from './hooks';
import { applyCuitMask } from 'utils/inputMasks/applyCuitMask';
// interfaces
import { IDocumentationProps } from './interfaces/DocumentationProps';
// styles
import styles from './Documentation.module.css';

// Componente de Input para manejar los campos de archivo y su lógica asociada
const FileUploader = ({ file, onChange, formValue, apiErrors }) => {
    const fileExists = formValue?.documents?.find((doc: any) => doc.document_type_id === file.document_type_id);
    const hasError = apiErrors[`documents.${file.document_type_id}`];

    return (
        <div className={styles['wrapper-files']}>
            <ExInputUploader
                type="text"
                name={file.name}
                label={file.name}
                isRequired={EX7767 ? file?.document_type_id === 5 ? false : formValue.documents.length > 0 : formValue.documents.length > 0}
                readOnly
                showErrorIcon={hasError || !!fileExists?.has_rejection}
                error={
                    !!fileExists?.has_rejection
                        ? t('documentation.error')
                        : hasError
                        ? apiErrors[`documents.${file.document_type_id}`][0]
                        : ''
                }
                placeholder={'Seleccionar archivo'}
                disabled={false}
                value={
                    fileExists?.file?.name ??
                    formValue?.documents?.find((doc: any) => doc.document_type_id === file.document_type_id)?.filename
                }
                onChange={(event) => onChange(event, file.document_type_id)}
                accept="image/png, image/jpeg, .pdf"
                startIcon={
                    <img
                        src={`/assets/images/${fileExists ? 'attach' : 'attach_disabled'}.svg`}
                        alt="attach icon"
                        draggable={false}
                    />
                }
            />
            <IconButton
                icon={<img src={'/assets/images/download_file_blue.svg'} alt="icon download" />}
                appearance="default"
                onClick={() => handleDownload(fileExists)}
                disabled={!fileExists}
                style={{
                    marginTop: !hasError ? 21 : '',
                }}
            />
        </div>
    );
};

// Componente principal
const Documentation = ({ formValue, onChange, country, apiErrors, setErrorResponse }: IDocumentationProps) => {
    const { FIELDS, onFileChange, handleChange, onFormInputChange } = useDocumentation({
        onChange,
        country,
        setErrorResponse,
    });

    const documentationRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (typeof window !== 'undefined' && window?.location?.hash === '#documentation' && documentationRef?.current) {
            documentationRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    }, []);

    return (
        <Panel className={styles['card']} id="documentation" ref={documentationRef}>
            <div className={styles['header']}>
                <p className={styles['title']}>{t('documentation.title')}</p>
                <p className={styles['subtitle']}>{t('documentation.subtitle')}</p>
            </div>
            <Form fluid className={styles['form-container']}>
                {/* Renderizar campos de formulario */}
                {FIELDS.map((field) => (
                    <FormInput
                        key={field.name}
                        type="text"
                        name={field.name}
                        label={field.name}
                        autoComplete="off"
                        isRequired
                        showErrorIcon={apiErrors[field.key]}
                        error={apiErrors[field.key]}
                        placeholder={field.placeholder}
                        disabled={false}
                        value={
                            !!formValue[field.key]
                                ? field.key === 'tax_id' && country === 'AR'
                                    ? applyCuitMask(formValue[field.key])
                                    : formValue[field.key]
                                : ''
                        }
                        onChange={(value) => handleChange(value, field.key)}
                        onKeyPress={(e) => {
                            if (field.key === 'tax_id' && country === 'AR') {
                                if (!/\d/.test(e.key)) {
                                    e.preventDefault();
                                }
                            }
                        }}
                    />
                ))}
                <div
                    className={styles['wrapper-docs']}
                    style={{ display: formValue?.required_documents?.length === 0 ? 'none' : '' }}
                >
                    {/* Renderizar los archivos */}
                    {formValue?.required_documents?.map((file) => {
                        if (file.name === 'GLN') {
                            // Función para obtener el documento por su ID
                            const document = formValue?.documents?.find(
                                (doc: any) => doc.document_type_id === file.document_type_id,
                            );

                            // Verificación de error para mostrar el icono
                            const showErrorIcon =
                                document?.has_rejection || apiErrors[`documents.${file.document_type_id}`];

                            // Mensaje de error
                            const error = document?.has_rejection
                                ? t('documentation.error')
                                : apiErrors[`documents.${file.document_type_id}`]
                                ? apiErrors[`documents.${file.document_type_id}`][0]
                                : '';

                            return (
                                <div className={styles['input-fullwidth']} key={file.name}>
                                    <FormInput
                                        type="text"
                                        name={file.name}
                                        label={file.name}
                                        extraLabel="13 dígitos"
                                        autoComplete="off"
                                        isRequired={formValue.documents.length > 0}
                                        showErrorIcon={showErrorIcon}
                                        error={error}
                                        placeholder="Ingresa el nombre del punto de venta"
                                        disabled={false}
                                        value={document?.file || ''}
                                        onChange={(value) => {
                                            if (value?.length <= 13) {
                                                onFormInputChange(value, file.document_type_id);
                                            }
                                        }}
                                        maxWidth={true}
                                        onKeyPress={(e) => {
                                            if (!/\d/.test(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                    />
                                </div>
                            );
                        } else {
                            return (
                                <FileUploader
                                    key={file.name}
                                    file={file}
                                    onChange={onFileChange}
                                    formValue={formValue}
                                    apiErrors={apiErrors}
                                />
                            );
                        }
                    })}
                </div>
            </Form>
        </Panel>
    );
};

export default Documentation;
